/**
 * ProjectGrid Component
 * PRJ-001: 프로젝트 카드 그리드 (SimScale 스타일)
 */

import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { School as TutorialIcon } from '@mui/icons-material';
import GridSortHeader, { type SortOrder } from '@/shared/ui/GridSortHeader';
import ProjectCard from '@/features/project-picker/ProjectCard';
import type { ProjectSummary } from '@/types/project';

// 정렬 옵션 타입
type SortField = 'updated_at' | 'created_at' | 'name';

// 정렬 라벨은 한글 통일 (2026-08-12 사용자 결정) — 영어는 내비 축 이름만
const SORT_OPTIONS: { value: SortField; label: string }[] = [
  { value: 'updated_at', label: '최근 수정' },
  { value: 'created_at', label: '생성일' },
  { value: 'name', label: '이름' },
];

interface ProjectGridProps {
  projects: ProjectSummary[];
  loading: boolean;
  error: string | null;
  /** 오류 카드의 «다시 시도» — 목록을 다시 조회한다(없으면 단추 없음) */
  onRetry?: () => void;
  searchQuery: string;
  onSelectProject: (projectId: string) => void;
  onDeleteProject: (projectId: string) => void;
  /** 워크스페이스 이동 — 지원 백엔드에서만 전달 (카드 메뉴에 노출) */
  onMoveProject?: (projectId: string) => void;
  /** 빈 목록 CTA — 튜토리얼(예제 설치 동선) 진입 */
  onStartTutorial?: () => void;
}

// 카드 메타 — 서버 요약(ProjectSummary)을 그대로 쓴다. 규칙(모델 수·전 모델 nodes 합·시뮬레이션 수·모델당 1칩의
// 분석 코드 '/' 결합)은 BFF ListProjects 와 summarizeProject 가 같은 값을 만든다. 목록은 본문을 싣지 않는다(#34).
const getProjectMetadata = (project: ProjectSummary) => ({
  modelCount: project.model_count,
  nodeCount: project.node_count,
  simulationCount: project.simulation_count,
  analysisCodes: project.analysis_codes,
});

const ProjectGrid: React.FC<ProjectGridProps> = ({
  projects,
  loading,
  error,
  onRetry,
  searchQuery,
  onSelectProject,
  onDeleteProject,
  onMoveProject,
  onStartTutorial,
}) => {
  // 정렬 상태
  const [sortField, setSortField] = useState<SortField>('updated_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // 정렬 순서 토글 핸들러
  const handleSortOrderToggle = () => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  // 필터링 + 정렬된 프로젝트 목록
  const sortedProjects = useMemo(() => {
    // 1. 검색 필터링
    let filtered = projects;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = projects.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.description?.toLowerCase().includes(query)
      );
    }

    // 2. 정렬
    const multiplier = sortOrder === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (sortField) {
        case 'updated_at':
          return multiplier * (new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime());
        case 'created_at':
          return multiplier * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        case 'name':
          return multiplier * a.name.localeCompare(b.name, 'ko');
        default:
          return 0;
      }
    });
  }, [projects, searchQuery, sortField, sortOrder]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ m: 2 }}
        action={onRetry && <Button color="inherit" size="small" onClick={onRetry}>다시 시도</Button>}
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ flex: 1, p: 1 }}>
      {/* 정렬 헤더 — Simulations 와 공용 컴포넌트 (L&F 통일 기준) */}
      <GridSortHeader
        options={SORT_OPTIONS}
        field={sortField}
        onFieldChange={setSortField}
        order={sortOrder}
        onOrderToggle={handleSortOrderToggle}
        countLabel={`${sortedProjects.length}개 프로젝트`}
      />

      {/* 프로젝트 그리드 */}
      {sortedProjects.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary">
            {searchQuery
              ? '검색 결과가 없습니다.'
              : '프로젝트가 없습니다. 새 프로젝트를 생성해주세요.'}
          </Typography>
          {!searchQuery && onStartTutorial && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                처음이라면 튜토리얼에서 예제 프로젝트를 설치하며 시작하세요.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<TutorialIcon />}
                onClick={onStartTutorial}
                sx={{ mt: 2, textTransform: 'none' }}
              >
                튜토리얼 시작하기
              </Button>
            </>
          )}
        </Box>
      ) : (
        <Grid container spacing={2}>
          {sortedProjects.map((project) => {
            const { modelCount, nodeCount, simulationCount, analysisCodes } = getProjectMetadata(project);

            return (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                xl={2.4}
                key={project.id}
              >
                <ProjectCard
                  variant="picker"
                  name={project.name}
                  count={nodeCount}
                  description={project.description || undefined}
                  updatedAt={project.updated_at}
                  onClick={() => onSelectProject(project.id)}
                  onSelect={() => onSelectProject(project.id)}
                  onDelete={() => onDeleteProject(project.id)}
                  onMove={onMoveProject ? () => onMoveProject(project.id) : undefined}
                  modelCount={modelCount}
                  simulationCount={simulationCount}
                  analysisCodes={analysisCodes}
                />
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default ProjectGrid;
